import { nullIfEmpty, parseJsonObject, readInstant, readString } from './json';
import { parseClaimKind, type HostClaimKind } from './hostLinkStatus';

/**
 * Why `POST /api/token` said no - port of `TokenRefusal.cs` (Plan-06 step 5c).
 * The three main answers mean three completely different things to three
 * different people, and a client that renders all of them as "connection
 * failed" sends someone to find an admin for a problem they could have solved
 * themselves.
 */
export type TokenRefusalKind =
  /** `409 in_use` (D23/§9.3) - someone else is training on this cell. The ONLY kind that must not be retried. */
  | 'in_use'
  /** `503 not_configured` - the wearer gate is not configured. A deployment problem. */
  | 'not_configured'
  /** `401`/`403` - the gate this client presented is wrong or stale (D21 makes it per-run). */
  | 'unauthorized'
  /** Some other HTTP status. The Function's own `{error, message}` is carried through. */
  | 'rejected'
  /** No answer at all: timeout, DNS, no route. */
  | 'unreachable';

/**
 * The `409 in_use` body (contract `inUseError`). Extends the surface's shared
 * `{error, message}` with the two facts a refusal needs to be actionable:
 * WHEN the current session started and WHO holds it.
 */
export interface HostInUseError {
  readonly message: string;
  /** When the current session started (epoch ms); may be absent from a malformed body. */
  readonly since: number | null;
  readonly sinceRaw: string;
  /** The display name the holder sent, when the DO recorded one. */
  readonly device: string | null;
  readonly cell: string;
  /** Whether the cell is held by a trainee or by an in-progress activation (§10.3). */
  readonly kind: HostClaimKind;
}

/** Parse a `409` body. Null unless it is genuinely the `in_use` shape. */
export function parseHostInUseError(json: string): HostInUseError | null {
  const root = parseJsonObject(json);
  if (!root) return null;
  if (readString(root, 'error') !== 'in_use') return null;
  return {
    message: readString(root, 'message'),
    sinceRaw: readString(root, 'since'),
    since: readInstant(root, 'since'),
    device: nullIfEmpty(readString(root, 'device')),
    cell: readString(root, 'cell'),
    kind: parseClaimKind(readString(root, 'kind')),
  };
}

/**
 * One classified answer from `POST /api/token`.
 *
 * `mayRetry` is D24 stated as a fact about the refusal rather than as a rule
 * someone has to remember: an `in_use` refusal is the one answer a client must
 * accept and stop on, because retrying it means racing a person who is
 * mid-training. Everything else may be retried - a stale gate or an
 * unconfigured deployment is fixed by a restart at the other end, and a retry
 * is how the client notices.
 */
export interface TokenRefusal {
  readonly kind: TokenRefusalKind;
  /** The HTTP status, or 0 when there was no answer. */
  readonly httpStatus: number;
  /** The Function's `error` slug (`in_use`, `not_configured`, ...); empty when absent. */
  readonly serverError: string;
  /** The Function's own `message`; empty when absent. */
  readonly serverMessage: string;
  /** Transport-level failure text (timeout, DNS), when there was no HTTP answer. */
  readonly transportError: string;
  /** The parsed `409` body - present only for `in_use`, and only when the body was well formed. */
  readonly inUse: HostInUseError | null;
  /** D24's rule as a fact: a refused trainee fails and STOPS on `in_use`. */
  readonly mayRetry: boolean;
}

/**
 * Classify one token response.
 * @param httpStatus The response code, or 0 when the request never got an answer.
 * @param body The response body, whatever arrived.
 * @param transportError The fetch layer's own error text, when there was no answer.
 */
export function classifyTokenRefusal(
  httpStatus: number,
  body: string | null | undefined,
  transportError?: string | null,
): TokenRefusal {
  // Not JSON - an edge worker's HTML error page, a proxy's plain text. The
  // status code still classifies it, and the raw body is logged by the caller.
  const envelope = body ? parseJsonObject(body) : null;
  const serverError = envelope ? readString(envelope, 'error') : '';
  const serverMessage = envelope ? readString(envelope, 'message') : '';

  const base = {
    httpStatus,
    serverError,
    serverMessage,
    transportError: transportError ?? '',
    inUse: null as HostInUseError | null,
  };

  if (httpStatus <= 0) {
    return { ...base, kind: 'unreachable', mayRetry: true };
  }

  switch (httpStatus) {
    case 409:
      // A malformed 409 is still a 409. The cell is busy either way, so the
      // client must still stop - it just cannot say when the session started.
      return { ...base, kind: 'in_use', inUse: body ? parseHostInUseError(body) : null, mayRetry: false };
    case 401:
    case 403:
      return { ...base, kind: 'unauthorized', mayRetry: true };
    case 503:
      return { ...base, kind: 'not_configured', mayRetry: true };
    default:
      // A 409 is the only status whose meaning this client decides by code
      // alone; for everything else the Function's own slug is the better
      // witness when it disagrees with the status.
      if (serverError === 'not_configured') return { ...base, kind: 'not_configured', mayRetry: true };
      if (serverError === 'unauthorized') return { ...base, kind: 'unauthorized', mayRetry: true };
      return { ...base, kind: 'rejected', mayRetry: true };
  }
}

/** A bounded, log-safe line naming the endpoint, the status and the Function's own words. */
export function describeTokenRefusal(refusal: TokenRefusal, endpoint?: string | null): string {
  const where = endpoint ? `POST ${endpoint}` : 'POST /api/token';
  if (refusal.kind === 'unreachable') {
    return `${where} did not answer (${refusal.transportError || 'no response'})`;
  }
  const slug = refusal.serverError || refusal.kind;
  const detail = refusal.serverMessage ? `: ${refusal.serverMessage}` : '';
  return `${where} answered ${refusal.httpStatus} ${slug}${detail}`;
}
